import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, Tag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { eur, apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, lineId } = useCart();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);

  const applyCoupon = async () => {
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}&subtotal=${subtotal}`);
      setCoupon(data);
      toast.success("Ο κωδικός προσφοράς εφαρμόστηκε!");
    } catch (e) { setCoupon(null); toast.error(apiErr(e)); }
  };

  const discount = coupon ? (coupon.type === "percentage" ? subtotal * coupon.value / 100 : coupon.type === "fixed" ? Math.min(coupon.value, subtotal) : 0) : 0;
  const freeShip = coupon?.type === "free_shipping" || subtotal - discount >= 80;
  const shipping = freeShip ? 0 : 3.5;
  const total = subtotal - discount + shipping;

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <ShoppingBag className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-3">Το καλάθι είναι άδειο</h1>
        <p className="text-zinc-400 mb-8">Ανακάλυψε τη νέα συλλογή SKGWEAR.</p>
        <Link to="/shop" className="btn-red inline-flex px-10 py-4 text-sm font-bold tracking-widest uppercase rounded">SHOP NOW</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-8">Καλάθι</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => {
            const id = lineId(it);
            return (
              <div key={id} className="flex gap-4 card-surface rounded-lg p-4" data-testid={`cart-page-item-${it.slug}`}>
                <Link to={`/product/${it.slug}`}><img src={it.image} alt={it.name} className="w-24 h-32 object-cover rounded bg-[#0A0A0B]" /></Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <Link to={`/product/${it.slug}`} className="font-display font-bold uppercase tracking-tight hover:text-[#FF2E4C]">{it.name}</Link>
                    <button onClick={() => removeItem(id)} className="text-zinc-600 hover:text-[#FF2E4C]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-xs font-mono text-zinc-500 uppercase mt-1">{it.color} / {it.size}</p>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center border border-zinc-700 rounded">
                      <button onClick={() => updateQty(id, it.quantity - 1)} className="p-2 hover:text-[#FF2E4C]"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="px-3 text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(id, Math.min(it.quantity + 1, it.max_stock || 99))} className="p-2 hover:text-[#FF2E4C]"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    <span className="font-semibold">{eur(it.price * it.quantity)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card-surface rounded-lg p-6 h-fit sticky top-28">
          <h2 className="font-display font-bold uppercase tracking-tight mb-4">Σύνοψη Παραγγελίας</h2>
          <div className="flex gap-2 mb-4">
            <div className="flex-1 flex items-center gap-2 bg-[#0A0A0B] border border-zinc-700 rounded px-3">
              <Tag className="w-4 h-4 text-zinc-500" />
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Κωδικός προσφοράς" className="flex-1 bg-transparent py-2.5 text-sm outline-none uppercase" data-testid="coupon-input-field" />
            </div>
            <button onClick={applyCoupon} className="px-4 bg-white text-black text-xs font-bold uppercase rounded hover:bg-zinc-200" data-testid="coupon-apply-button">ΕΦΑΡΜΟΓΗ</button>
          </div>
          <div className="space-y-2.5 text-sm border-t border-zinc-800 pt-4">
            <div className="flex justify-between"><span className="text-zinc-400">Υποσύνολο</span><span>{eur(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#FF2E4C]"><span>Έκπτωση ({coupon.code})</span><span>-{eur(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-400">Μεταφορικά</span><span>{freeShip ? "ΔΩΡΕΑΝ" : eur(shipping)}</span></div>
            <div className="flex justify-between font-display font-black text-lg border-t border-zinc-800 pt-3 mt-3"><span>ΣΥΝΟΛΟ</span><span data-testid="cart-total">{eur(total)}</span></div>
          </div>
          <button onClick={() => navigate("/checkout")} className="btn-red w-full mt-5 py-4 text-sm font-bold tracking-widest uppercase rounded flex items-center justify-center gap-2" data-testid="cart-page-checkout-btn">
            ΟΛΟΚΛΗΡΩΣΗ <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/shop" className="block text-center mt-3 text-xs font-mono uppercase text-zinc-400 hover:text-white">← Συνέχεια αγορών</Link>
        </div>
      </div>
    </div>
  );
}
